// src/pages/Contact.jsx
import { useState } from "react";
import { supabase } from "../supabaseClient";

const CONTACT_EMAIL = "cpennell16@gmail.com"; // shown only for mailto fallback

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    website: "", // honeypot
  });
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setStatus({ type: "", msg: "" });

    // Honeypot (bots will fill this)
    if (form.website.trim()) return;

    // Super-light rate limit (30s)
    const last = Number(localStorage.getItem("contact_last_ts") || 0);
    if (Date.now() - last < 30_000) {
      setStatus({
        type: "error",
        msg: "Please wait a moment before sending another message.",
      });
      return;
    }

    // Basic validation
    if (!form.name || !form.email || !form.message) {
      setStatus({
        type: "error",
        msg: "Name, email, and message are required.",
      });
      return;
    }

    setLoading(true);
    try {
      // 1) Save to Supabase
      const { error } = await supabase.from("contact_messages").insert({
        name: form.name,
        email: form.email,
        subject: form.subject || null,
        message: form.message,
        user_agent: navigator.userAgent || null,
      });
      if (error) throw error;

      // 2) Fire-and-forget email via Netlify Function
      fetch("/.netlify/functions/send-contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          subject: form.subject,
          message: form.message,
        }),
      }).catch(() => {
        // ignore email errors here; we already saved the message
      });

      localStorage.setItem("contact_last_ts", String(Date.now()));
      setStatus({ type: "success", msg: "Thanks! Your message was sent." });
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (err) {
      // Fallback mailto if Supabase insert fails
      setStatus({
        type: "error",
        msg: `Couldn’t send via form. You can email us directly at ${CONTACT_EMAIL}.`,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto flex max-w-3xl flex-col px-4 py-10 sm:py-14">
        {/* Header */}
        <header className="mb-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/40 bg-slate-950/80 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-sky-100 shadow-[0_0_22px_rgba(56,189,248,0.4)]">
            <span className="h-1.5 w-1.5 rounded-full bg-sky-300" />
            Get in touch
          </div>

          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl">
            Contact
          </h1>
          <p className="mt-2 max-w-xl text-sm text-slate-300">
            Questions, bug reports, or feature requests? Drop a message below or email{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="font-medium text-sky-300 hover:text-sky-200"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </header>

        {/* Card */}
        <div className="rounded-2xl border border-slate-800/80 bg-slate-950/90 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.9)] sm:p-8">
          {/* Status */}
          {status.msg && (
            <div
              className={`mb-5 rounded-xl px-4 py-3 text-sm ${
                status.type === "success"
                  ? "bg-emerald-500/10 text-emerald-200 ring-1 ring-emerald-500/40"
                  : "bg-red-500/10 text-red-200 ring-1 ring-red-500/40"
              }`}
            >
              {status.msg}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            {/* Honeypot */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={onChange}
              className="hidden"
              autoComplete="off"
              tabIndex={-1}
            />

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-300">
                Name
              </label>
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                placeholder="Your name"
                disabled={loading}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-300">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                placeholder="you@example.com"
                disabled={loading}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-300">
                Subject <span className="text-slate-500">(optional)</span>
              </label>
              <input
                name="subject"
                value={form.subject}
                onChange={onChange}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                placeholder="What’s this about?"
                disabled={loading}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-300">
                Message
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={6}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                placeholder="How can we help?"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center rounded-full bg-sky-500 px-5 py-2 text-sm font-semibold text-slate-950 shadow hover:bg-sky-400 disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send message"}
            </button>
          </form>

          <div className="mt-8 border-t border-slate-800/80 pt-4 text-xs text-slate-400">
            <p>
              For privacy details see{" "}
              <a href="/legal" className="font-medium text-sky-300 hover:text-sky-200">
                Privacy &amp; Terms
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
